import { addDays, format, parseISO, subDays } from 'date-fns';
import type {
    CampaignInsertionOrder,
    CampaignInsertionOrderPlaceholder,
    InsertionOrderCondition,
    InsertionOrderKey,
    InsertionOrderRule,
    InsertionOrderRulePayload,
} from 'c-sdk';
import {
    InsertionOrderAttribute,
    InsertionOrderSearchType,
    PLACEHOLDER_CUSTOMER,
    placeholderScheduleKey,
} from 'c-sdk';

/**
 * UL-7912 -- the pure logic behind the DSP schedule page.
 *
 * Kept free of React so the rules that decide what gets saved can be tested on their own. Several
 * of these have to agree with the API exactly, and say so where they do.
 */

/** The longest identifier the API accepts. */
export const MAX_IDENTIFIER_LENGTH = 255;

/** Days either side of the campaign that a date bound defaults to. */
export const DEFAULT_BOUND_PADDING_DAYS = 14;

// ---------------------------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------------------------

/**
 * An insertion order's identity. An id is only unique within a DSP, so every map and every lookup is
 * keyed on both halves -- the API's `catalogueKey()` uses the same `customer|id` shape.
 */
export const insertionOrderKey = (io: InsertionOrderKey): string =>
    `${io.customer}|${io.insertion_order_id}`;

// ---------------------------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------------------------

/**
 * The campaign's dates plus two weeks either side, as `yyyy-MM-dd`.
 *
 * Only the date part is used. Campaign dates arrive as timestamps, and arithmetic on a full
 * timestamp in the viewer's local zone can land a day out either side of midnight.
 */
export const defaultInsertionOrderDateRange = (
    dateStart?: string | null,
    dateEnd?: string | null,
): [string, string] | null => {
    if (!dateStart || !dateEnd) return null;

    const start = parseISO(dateStart.slice(0, 10));
    const end = parseISO(dateEnd.slice(0, 10));

    return [
        format(subDays(start, DEFAULT_BOUND_PADDING_DAYS), 'yyyy-MM-dd'),
        format(addDays(end, DEFAULT_BOUND_PADDING_DAYS), 'yyyy-MM-dd'),
    ];
};

/**
 * Catalogue filters for "played at any point inside this range".
 *
 * OVERLAP, and deliberately the same two comparisons the API resolver uses for a rule's date bound:
 * the last playout is on or after the start, and the first playout is on or before the end. If
 * these ever diverge, the selection modal shows insertion orders a rule with the same dates would
 * not attach.
 */
export const catalogueOverlapFilters = (
    from?: string | null,
    to?: string | null,
): Record<string, string[]> => {
    const filters: Record<string, string[]> = {};

    if (from) filters['filter.max_date'] = [`>=${from.slice(0, 10)}`];
    if (to) filters['filter.min_date'] = [`<=${to.slice(0, 10)}`];

    return filters;
};

// ---------------------------------------------------------------------------------------------
// Edit Table Parameters: several tabs, one Apply
// ---------------------------------------------------------------------------------------------

/** One tab of the parameters modal as the user has left it. */
export type TabDraft = {
    attribute: InsertionOrderAttribute;
    searchType: InsertionOrderSearchType;
    /** Raw textarea content, one identifier per line. */
    text: string;
};

export type BuildRulesInput = {
    tabs: TabDraft[];
    customer: string | null;
    dateFrom: string | null;
    dateTo: string | null;
};

export type BuildRulesResult =
    | { ok: true; rules: InsertionOrderRulePayload[] }
    | { ok: false; error: BuildRulesError };

export type BuildRulesError =
    | { code: 'nothing_entered' }
    | { code: 'id_must_be_exact' }
    | { code: 'identifier_too_long'; identifier: string }
    | { code: 'dates_reversed' }
    | { code: 'several_multi_line_tabs'; attributes: InsertionOrderAttribute[] };

/**
 * Trimmed, blank-free, de-duplicated lines, in the order typed.
 *
 * Case-sensitive on purpose: the API's duplicate check hashes identifiers case-sensitively, so
 * folding case here would hide a rule the API considers distinct.
 */
export const normaliseLines = (text: string): string[] => {
    const seen = new Set<string>();
    const lines: string[] = [];

    text.split('\n').forEach(raw => {
        const line = raw.trim();

        if (line !== '' && !seen.has(line)) {
            seen.add(line);
            lines.push(line);
        }
    });

    return lines;
};

/**
 * Turn the modal's tabs into the rules to save.
 *
 * **Lines within a tab are OR'd; filled tabs are AND'ed.** So:
 * - one filled tab            -> one rule per line, exactly as Media works today
 * - several single-line tabs  -> one rule whose conditions are all of them
 * - one multi-line tab plus single-line tabs -> a rule per line of the multi-line tab, each also
 *   carrying the single-line tabs' conditions
 * - two or more multi-line tabs -> refused. The alternative is a silent cross product, which is
 *   never what anyone typing into two boxes expects.
 */
export const buildRulePayloads = ({
    tabs,
    customer,
    dateFrom,
    dateTo,
}: BuildRulesInput): BuildRulesResult => {
    const filled = tabs
        .map(tab => ({ tab, lines: normaliseLines(tab.text) }))
        .filter(({ lines }) => lines.length > 0);

    if (filled.length === 0) return { ok: false, error: { code: 'nothing_entered' } };

    const idTab = filled.find(
        ({ tab }) => tab.attribute === InsertionOrderAttribute.insertionOrderId,
    );
    if (idTab && idTab.tab.searchType !== InsertionOrderSearchType.exact) {
        return { ok: false, error: { code: 'id_must_be_exact' } };
    }

    const tooLong = filled
        .flatMap(({ lines }) => lines)
        .find(line => line.length > MAX_IDENTIFIER_LENGTH);
    if (tooLong) return { ok: false, error: { code: 'identifier_too_long', identifier: tooLong } };

    if (dateFrom && dateTo && dateFrom.slice(0, 10) > dateTo.slice(0, 10)) {
        return { ok: false, error: { code: 'dates_reversed' } };
    }

    const multiLine = filled.filter(({ lines }) => lines.length > 1);
    if (multiLine.length > 1) {
        return {
            ok: false,
            error: {
                code: 'several_multi_line_tabs',
                attributes: multiLine.map(({ tab }) => tab.attribute),
            },
        };
    }

    const condition = (tab: TabDraft, identifier: string): InsertionOrderCondition => ({
        parameter: tab.attribute,
        search_type: tab.searchType,
        identifier,
    });

    const singles = filled
        .filter(({ lines }) => lines.length === 1)
        .map(({ tab, lines }) => condition(tab, lines[0]));

    const shared = {
        customer: customer && customer !== 'any' ? customer : null,
        date_from: dateFrom ? dateFrom.slice(0, 10) : null,
        date_to: dateTo ? dateTo.slice(0, 10) : null,
    };

    if (multiLine.length === 0) {
        return { ok: true, rules: [{ ...shared, conditions: singles }] };
    }

    const [spread] = multiLine;

    return {
        ok: true,
        rules: spread.lines.map(line => ({
            ...shared,
            conditions: [condition(spread.tab, line), ...singles],
        })),
    };
};

// ---------------------------------------------------------------------------------------------
// Describing rules
// ---------------------------------------------------------------------------------------------

const ATTRIBUTE_LABELS: Record<InsertionOrderAttribute, string> = {
    [InsertionOrderAttribute.insertionOrderId]: 'ID',
    [InsertionOrderAttribute.insertionOrderName]: 'name',
    [InsertionOrderAttribute.advertiser]: 'advertiser',
};

const SEARCH_TYPE_LABELS: Record<InsertionOrderSearchType, string> = {
    [InsertionOrderSearchType.exact]: 'exact',
    [InsertionOrderSearchType.contains]: 'contains',
    [InsertionOrderSearchType.startsWith]: 'starts with',
    [InsertionOrderSearchType.endsWith]: 'ends with',
};

/**
 * A rule the way the mockup's "Attached by" column reads it: `name contains "National Park"`, or
 * `ID exact` for a pick -- the id is already in its own column, so repeating it is noise.
 */
export const describeRule = (rule: Pick<InsertionOrderRule, 'conditions'>): string =>
    rule.conditions
        .map(({ parameter, search_type: searchType, identifier }) =>
            parameter === InsertionOrderAttribute.insertionOrderId
                ? `${ATTRIBUTE_LABELS[parameter]} ${SEARCH_TYPE_LABELS[searchType]}`
                : `${ATTRIBUTE_LABELS[parameter]} ${SEARCH_TYPE_LABELS[searchType]} “${identifier}”`,
        )
        .join(' and ');

/** Every rule that attached an insertion order, described, in rule order. */
export const attachedByLabels = (
    parameterIds: number[],
    rulesById: Map<number, InsertionOrderRule>,
): string[] =>
    parameterIds
        .map(id => rulesById.get(id))
        .filter((rule): rule is InsertionOrderRule => rule !== undefined)
        .map(describeRule);

// ---------------------------------------------------------------------------------------------
// Insertion Order Selection: picks
// ---------------------------------------------------------------------------------------------

/**
 * Is this rule a manual pick -- exactly one exact condition on the id, scoped to one DSP, with no
 * date bound? That is precisely what ticking a row saves, so it is what un-ticking may remove.
 */
export const isPickRule = (rule: InsertionOrderRule): boolean =>
    rule.customer !== null &&
    rule.date_from === null &&
    rule.date_to === null &&
    rule.conditions.length === 1 &&
    rule.conditions[0].parameter === InsertionOrderAttribute.insertionOrderId &&
    rule.conditions[0].search_type === InsertionOrderSearchType.exact;

/** Pick rules by the insertion order they pick. */
export const pickRulesByKey = (rules: InsertionOrderRule[]): Map<string, InsertionOrderRule> => {
    const picks = new Map<string, InsertionOrderRule>();

    rules.filter(isPickRule).forEach(rule => {
        picks.set(
            insertionOrderKey({
                customer: rule.customer as string,
                insertion_order_id: rule.conditions[0].identifier,
            }),
            rule,
        );
    });

    return picks;
};

/** The rule a tick saves. No date bound: an explicitly chosen insertion order attaches regardless. */
export const pickRulePayload = (io: InsertionOrderKey): InsertionOrderRulePayload => ({
    customer: io.customer,
    date_from: null,
    date_to: null,
    conditions: [
        {
            parameter: InsertionOrderAttribute.insertionOrderId,
            search_type: InsertionOrderSearchType.exact,
            identifier: io.insertion_order_id,
        },
    ],
});

/**
 * What Apply has to do: create a pick for each newly ticked row, delete the pick for each un-ticked
 * one. Rows attached only by a broader rule have no pick to delete, and are left alone.
 */
export const diffPicks = (
    ticked: InsertionOrderKey[],
    unticked: InsertionOrderKey[],
    rules: InsertionOrderRule[],
): { toCreate: InsertionOrderRulePayload[]; toDeleteRuleIds: number[] } => {
    const picks = pickRulesByKey(rules);

    return {
        toCreate: ticked.filter(io => !picks.has(insertionOrderKey(io))).map(pickRulePayload),
        toDeleteRuleIds: unticked
            .map(io => picks.get(insertionOrderKey(io))?.id)
            .filter((id): id is number => id !== undefined),
    };
};

// ---------------------------------------------------------------------------------------------
// The insertion order table
// ---------------------------------------------------------------------------------------------

/**
 * A table row: an attached insertion order, or (UL-8136) a planned line standing in for one. A
 * planned line carries its reserved schedule key as `customer` / `insertion_order_id`, so ticking,
 * scheduling and the schedule chip's ✕ all work on it unchanged.
 */
export type InsertionOrderTableRow = CampaignInsertionOrder & {
    placeholder?: CampaignInsertionOrderPlaceholder;
};

export const placeholderRow = (
    placeholder: CampaignInsertionOrderPlaceholder,
    campaignId: number,
): InsertionOrderTableRow => ({
    ...placeholderScheduleKey(placeholder.id),
    id: placeholder.id,
    campaign_id: campaignId,
    insertion_order_name: placeholder.label,
    advertiser: null,
    min_date: null,
    max_date: null,
    parameter_ids: [],
    schedule_id: placeholder.schedule_id,
    cpm: null,
    impressions_target: placeholder.impressions_target,
    created_at: placeholder.created_at,
    updated_at: null,
    placeholder,
});

// ---------------------------------------------------------------------------------------------
// Missing targets (UL-7967)
// ---------------------------------------------------------------------------------------------

/**
 * Whether a row is flagged for having no impressions target. Only `null` (blank) is missing: a
 * target of 0 was typed and counts. Planned lines are not insertion orders, so never flagged.
 */
export const isMissingTarget = (
    row: Pick<InsertionOrderTableRow, 'impressions_target' | 'placeholder' | 'customer'>,
): boolean =>
    !row.placeholder &&
    row.customer !== PLACEHOLDER_CUSTOMER &&
    (row.impressions_target === null || row.impressions_target === undefined);

/**
 * "N of M insertion orders have a target". M is the distinct attached insertion orders (planned
 * lines excluded), N those with a target (0 included). Read from every attached IO, not the
 * filtered rows, so a filter can't hide a missing target from the count.
 */
export const targetCoverage = (
    insertionOrders: Pick<
        CampaignInsertionOrder,
        'customer' | 'insertion_order_id' | 'impressions_target'
    >[],
): { withTarget: number; total: number } => {
    const hasTarget = new Map<string, boolean>();
    insertionOrders.forEach(io => {
        if (io.customer === PLACEHOLDER_CUSTOMER) return;
        const key = insertionOrderKey(io);
        const typed = io.impressions_target !== null && io.impressions_target !== undefined;
        hasTarget.set(key, (hasTarget.get(key) ?? false) || typed);
    });

    let withTarget = 0;
    hasTarget.forEach(typed => {
        if (typed) withTarget += 1;
    });

    return { withTarget, total: hasTarget.size };
};

export type InsertionOrderTableFilters = {
    customer: string | null;
    advertiser: string | null;
    nameContains: string;
    /** A schedule id, or null for any. */
    scheduleId: number | null;
};

export const filterInsertionOrders = <Row extends CampaignInsertionOrder>(
    rows: Row[],
    { customer, advertiser, nameContains, scheduleId }: InsertionOrderTableFilters,
): Row[] => {
    const needle = nameContains.trim().toLowerCase();

    return rows.filter(
        row =>
            (!customer || row.customer === customer) &&
            (!advertiser || row.advertiser === advertiser) &&
            (needle === '' || (row.insertion_order_name ?? '').toLowerCase().includes(needle)) &&
            (scheduleId === null || row.schedule_id === scheduleId),
    );
};

const csvCell = (value: unknown): string => {
    const text = value === null || value === undefined ? '' : String(value);

    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** The table as a CSV, with the rule descriptions resolved rather than bare ids. */
export const insertionOrdersToCsv = (
    rows: CampaignInsertionOrder[],
    rulesById: Map<number, InsertionOrderRule>,
    scheduleNames: Map<number, string>,
): string => {
    const header = [
        'Name',
        'Insertion order ID',
        'Customer',
        'Advertiser',
        'Min date',
        'Max date',
        'CPM',
        'Attached by',
        'Schedule',
    ];

    const lines = rows.map(row =>
        [
            row.insertion_order_name,
            row.insertion_order_id,
            row.customer,
            row.advertiser,
            row.min_date,
            row.max_date,
            row.cpm,
            attachedByLabels(row.parameter_ids, rulesById).join('; '),
            row.schedule_id === null ? '' : scheduleNames.get(row.schedule_id) ?? row.schedule_id,
        ]
            .map(csvCell)
            .join(','),
    );

    return [header.join(','), ...lines].join('\n');
};
